import { useEffect, useRef, useState } from "react";
import { useSocket } from "@data-phone/react-resource-server-auth";
import { useSelector } from "react-redux";
import {
  addOrUpdateLiveCall,
  LiveCall,
  removeLiveCall,
  setLiveCalls,
} from "../redux/slices/liveCalls.slice";
import { updateAgentStatus } from "../redux/slices/agents.slice";
import { RootState, store } from "../redux/store";
import { useAppDispatch } from "../redux/hooks";
import { useIntegrationLookups } from "./useIntegrationLookups";
import { isPossiblePhoneNumber } from "utils/isPossiblePhoneNumber";
import { phoneNumberFormatE164 } from "utils/phoneNumberFormatE164";
import { useHistoryLookups } from "./useHistoryLookups";
import { getAgentStatus } from "utils";

interface Callee {
  uuid: string;
  number: string;
  answerState: string;
}

interface CallFlowEntry {
  time: string;
  number: string;
}

interface WebSocketCallData {
  callUUID: string;
  callerIDName?: string;
  callerIDNumber?: string;
  startTime?: string;
  answerState?: string;
  answerTime?: string;
  dialedNumber?: string;
  destination?: string;
  callDirection?: "inbound" | "outbound";
  callFlow?: CallFlowEntry[];
  isHolding?: boolean;
  isParked?: boolean;
  isRecording?: boolean;
  callees?: Callee[];
}

interface CallbackResponse<T = any> {
  code: number;
  message: string;
  data?: T;
  error?: string;
}

export const useCallSocket = () => {
  const { fetchCallHistory } = useHistoryLookups();
  const dispatch = useAppDispatch();
  const {fetchIntegrationLookups} = useIntegrationLookups();

  const { socket, changeAccount } = useSocket({
    socketUrl: `${process.env.REACT_APP_LIVE_CALL_WSS}/activeCalls`,
    path: "",
  });

  const liveCalls = useSelector((state: RootState) => state.liveCalls.calls);
  const { account_id } = useSelector((state: RootState) => state.appConfig);
  const integrations = useSelector((state: RootState) => state.integrations.items);

  const [initialCallsToLookup, setInitialCallsToLookup] = useState<string[]>([]);
  const liveCallsRef = useRef<LiveCall[]>([]);
  liveCallsRef.current = liveCalls;

  const isFirstRun = useRef(true);

  // 🌐 Trigger lookup after integrations are ready
  useEffect(() => {
    if (
      integrations.length > 0 &&
      integrations.every((item) => item.config && Object.keys(item.config).length > 0) &&
      initialCallsToLookup.length > 0
    ) {
      fetchIntegrationLookups(initialCallsToLookup);
      setInitialCallsToLookup([]);
    }
  }, [integrations, initialCallsToLookup]);

  useEffect(() => {
  if (!socket || !account_id) return;

  
  changeAccount(account_id); // ✅ always call this

  // ⏳ Delay get-calls to allow backend to register account change
  setTimeout(() => {
    getCalls();
  }, 200);

  isFirstRun.current = false;
}, [account_id, socket]);



  const handleRecord = (callUUID: string, action: "start" | "stop"): void => {
    try {
      if (socket) {
        socket.emit("record", callUUID, action, (err?: Error) => {
          if (err) {
            console.error("❌ Error during record emit:", err);
          }
        });
      }
    } catch (error) {
      console.error("❌ Error in handleRecord:", error);
    }
  };

  const getCalls = () => {
  if (!socket) return;

  socket.emit("get-calls", (res: CallbackResponse<LiveCall[]>) => {
    
    try {
      if (res.data) {
        if (!Array.isArray(res.data)) throw new Error("get-calls payload is not an array");
        const liveCalls: LiveCall[] = res.data.map(normalizeCall);
        dispatch(setLiveCalls(liveCalls));
        res.data.forEach((call) => {updateAgentStatuses(call.callees)
          
        });

        // 🔍 Collect numbers for HHA lookup
        const numbersToLookup = res.data
  .map((item) => item.callerIDNumber)
  .filter((num): num is string => typeof num === 'string' && isPossiblePhoneNumber(num, 'US'))
  .map((num) => phoneNumberFormatE164(num))
  .filter((num): num is string => typeof num === 'string');

setInitialCallsToLookup(numbersToLookup);
      } else {
        console.error("❌ Error fetching calls:", res.error);
      }
    } catch (error) {
      console.error("❌ Error handling get-calls:", error);
    }
  });
};


  

  const normalizeCall = (call: WebSocketCallData): LiveCall => ({
    callUUID: call.callUUID,
    callerIDName: call.callerIDName ?? "",
    callerIDNumber: call.callerIDNumber?.trim() ?? "",
    startTime: call.startTime ?? new Date().toISOString(),
    duration: "",
    answerState: call.answerState ?? "",
    callees: call.callees ?? [],
    callDirection: call.callDirection ?? "inbound",
    callFlow: call.callFlow ?? [],
    destination: call.destination?.trim() ?? "",
    dialedNumber: call.dialedNumber ?? "",
    isHolding: !!call.isHolding,
    isParked: !!call.isParked,
    isRecording: !!call.isRecording,
    answerTime: call.answerTime ?? "",
  });

  const updateAgentStatuses = (callees?: Callee[]) => {
    try {
      if (!callees) return;

      const agents = store.getState().agents.items;
      if (!agents || agents.length === 0) return;

      callees.forEach((callee) => {
        const extension = callee.number?.trim();
        const matchingAgent = agents.find((a) => a.extension.trim() === extension);

        if (!matchingAgent) {
          console.warn("❌ No agent matched for extension:", extension);
          return;
        }

        const agentStatus = getAgentStatus(callee.answerState);

dispatch(
  updateAgentStatus({
    id: matchingAgent.id,
    status: agentStatus,
    state: callee.answerState,
  })
);
      });
    } catch (error) {
      console.error("❌ Error in updateAgentStatuses:", error);
    }
  };

  useEffect(() => {
    if (!socket) return;

    socket.on("connect", () => console.log("✅ Socket.IO connected"));
    socket.on("disconnect", () => console.log("🔌 Socket.IO disconnected"));
    socket.on("connect_error", (err) => console.error("❌ Connect error:", err));

    socket.on("call-update", (updatedCall: WebSocketCallData) => {
  try {
    if (!updatedCall || typeof updatedCall !== "object") throw new Error("Invalid call-update payload");

    const normalized = normalizeCall(updatedCall);

    // 🔴 Check if answerState is hangup, then remove
    if (normalized.answerState?.toLowerCase() === "hangup") {
      dispatch(removeLiveCall(normalized.callUUID));
      updateAgentStatuses(updatedCall.callees); // Set agents back to available
      return; // Skip adding/updating the call
    }

    dispatch(addOrUpdateLiveCall(normalized));
    updateAgentStatuses(updatedCall.callees);

    // 🔍 Integration Lookup
    if (
      updatedCall?.callerIDNumber &&
      isPossiblePhoneNumber(updatedCall.callerIDNumber, "US")
    ) {
      const formatted = phoneNumberFormatE164(updatedCall.callerIDNumber);
      if (formatted) {
        fetchIntegrationLookups(formatted); // ✅ HHA data
      }
    }

  } catch (error) {
    console.error("❌ Error handling call-update:", error);
  }
});


    socket.on("call-destroy", (payload: any) => {
      try {
        const callUUID = typeof payload === "string" ? payload : payload?.callUUID;
        if (!callUUID) throw new Error("Invalid call-destroy payload");

        const removedCall = liveCallsRef.current.find((call) => call.callUUID === callUUID);
        const callees = removedCall?.callees ?? [];

        const agents = store.getState().agents.items;
        callees.forEach((callee) => {
          const matchingAgent = agents.find((a) => a.extension.trim() === callee.number.trim());
          if (matchingAgent) {
            dispatch(
              updateAgentStatus({
                id: matchingAgent.id,
                status: "Available",
                state: "idle",
              })
            );
          }
        });

        dispatch(removeLiveCall(callUUID));
      } catch (error) {
        console.error("❌ Error handling call-destroy:", error);
      }
    });

    /*setTimeout(() => {
        const fakeCall: WebSocketCallData = {
          callUUID: "abbab564-e56e-49e2-b085-e755fb0ede42",
          callerIDName:"Arnold Smith",
          callerIDNumber:"+13475815620",
          startTime: new Date().toISOString(),
          answerState: "answered",
          destination: "90003",
          callDirection: "inbound",
          isRecording: false,
          callees: [
            {
              uuid: "f100e213-1f8d-44c0-b8f6-03ca0a0f569c",
              number: "9701",
              answerState: "answered",
            },
          ],
        };

        console.log("🚀 Simulating fake call-update event...");
        dispatch(addOrUpdateLiveCall(normalizeCall(fakeCall)));
        updateAgentStatuses(fakeCall.callees);
      }, 3000);*/

    return () => {
      socket.off("call-update");
      socket.off("call-destroy");
      socket.disconnect();
    };
  }, [socket, dispatch]);

  

  return { handleRecord };
};
